// Decisões puras da efetivação de acordo de parcelamento. Chamado #5686.
//
// Separado do index.ts da edge function de propósito: é a regra que decide se um
// acordo passa a valer — e efetivar é o ato que CANCELA os boletos originais do
// cliente. Uma regra dessas não pode depender de banco para ser testada.
//
// Sem I/O, sem Deno, sem Supabase: roda sob `node --test`.

export type StatusAcordo =
  | 'aguardando_aprovacao' | 'aguardando_assinatura' | 'aguardando_entrada'
  | 'ativo' | 'em_atraso' | 'concluido' | 'cancelado' | 'expirado';

export type Acao =
  | 'nada'
  | 'marcar_assinado'   // assinou -> aguardando_entrada
  | 'efetivar'          // assinou E entrada paga -> ativo
  | 'expirar'           // passou do prazo sem completar
  | 'segurar_sem_assinatura' // passou do prazo com a entrada paga: não expira, avisa (#6342)
  | 'marcar_atraso'     // ativo com parcela vencida
  | 'sair_do_atraso';   // em_atraso sem parcela vencida (cliente se acertou)

export interface EstadoAssinatura {
  /** false quando o documento nem foi criado ainda. */
  temDocumento: boolean;
  assinadoEm: string | null;
  /** Biometria reprovada / assinatura recusada. Bloqueia a efetivação. */
  rejeitadoEm: string | null;
}

/** Assinatura de UMA parte (devedor ou coobrigado), como `assinaturaDe` devolve. */
export interface AssinaturaParte {
  assinadoEm: string | null;
  rejeitadoEm: string | null;
}

/**
 * Junta as assinaturas das partes do acordo num estado só.
 *
 * Com coobrigado (quem assume a dívida — ex.: comprou o estabelecimento), o
 * acordo só vale quando os DOIS assinam: a Cláusula 7.6 do termo diz que a
 * responsabilidade dele depende da assinatura em nome próprio, e efetivar com
 * só o titular deixaria o acordo valendo sem quem de fato vai pagar.
 *
 * Recusa de qualquer um bloqueia. Assinado = a ÚLTIMA das assinaturas — é
 * quando o acordo ficou completo.
 */
export function combinarAssinaturas(partes: AssinaturaParte[]): { assinadoEm: string | null; rejeitadoEm: string | null } {
  const recusa = partes.find((p) => p.rejeitadoEm);
  if (recusa) return { assinadoEm: null, rejeitadoEm: recusa.rejeitadoEm };
  if (partes.length === 0 || partes.some((p) => !p.assinadoEm)) return { assinadoEm: null, rejeitadoEm: null };
  const ultima = partes
    .map((p) => p.assinadoEm as string)
    .reduce((a, b) => (Date.parse(b) > Date.parse(a) ? b : a));
  return { assinadoEm: ultima, rejeitadoEm: null };
}

export interface EstadoEntrada {
  /** false quando a cobrança da entrada nem chegou a ser gerada. */
  temCobranca: boolean;
  pago: boolean;
  pagoEm: string | null;
}

export interface AcordoParaAvaliar {
  id: string;
  status: StatusAcordo;
  expiraEm: string | null;
  /** Vencimento do boleto da entrada (YYYY-MM-DD). Segura a expiração — ver
   *  CARENCIA_COMPENSACAO_DIAS. */
  entradaVencimento?: string | null;
  termoAssinadoEm: string | null;
  entradaPagaEm: string | null;
  /** Vencimentos das parcelas (tipo 'parcela') ainda não pagas nem canceladas. */
  vencimentosEmAberto: string[];
}

/**
 * Dias depois do vencimento da entrada em que a proposta NÃO expira, mesmo com o
 * prazo de validade vencido.
 *
 * Boleto pago no vencimento compensa em D+1 a D+2 úteis, e a baixa só chega ao
 * banco na sincronização seguinte — com fim de semana no meio, são até 5 dias
 * corridos. Sem esta folga, o cliente que pagou a entrada no último dia via o
 * acordo expirar antes de o pagamento aparecer: dinheiro recebido, acordo morto
 * e a dívida antiga de volta. Também cobre a entrada marcada para depois do fim
 * do prazo de validade, que expiraria antes mesmo de vencer.
 */
export const CARENCIA_COMPENSACAO_DIAS = 5;

/** Soma dias a uma data YYYY-MM-DD sem passar por fuso. */
export function somarDias(dataIso: string, dias: number): string {
  const [a, m, d] = dataIso.slice(0, 10).split('-').map(Number);
  const t = new Date(Date.UTC(a, m - 1, d + dias));
  return t.toISOString().slice(0, 10);
}

/** Último dia (YYYY-MM-DD) em que a entrada ainda pode estar compensando. */
export function limiteCompensacaoEntrada(entradaVencimento: string): string {
  return somarDias(entradaVencimento, CARENCIA_COMPENSACAO_DIAS);
}

export interface Decisao {
  acao: Acao;
  motivo: string;
  /** Preenchido quando a ação é 'marcar_assinado' ou 'efetivar'. */
  assinadoEm?: string | null;
  entradaPagaEm?: string | null;
}

/** Comparação de datas por ISO, sem construir Date: string ISO ordena
 *  lexicograficamente e não sofre com fuso. */
const venceuAte = (data: string, agora: string) => data.slice(0, 10) < agora.slice(0, 10);

/**
 * O que fazer com este acordo agora.
 *
 * A ordem das checagens importa:
 *
 *  1. Rejeição de assinatura vem antes de tudo — biometria reprovada invalida o
 *     ato, e efetivar nesse estado registraria um fato falso.
 *  2. Expiração só alcança quem NÃO completou. Quem assinou e pagou tem direito
 *     ao acordo mesmo que a compensação tenha chegado depois do prazo — o
 *     cliente cumpriu a parte dele. O risco de o cliente ter pago também uma
 *     fatura original no meio tempo não é tratado aqui: `conferirFaturasMae`
 *     barra a efetivação nesse caso, dentro ou fora do prazo.
 *  3. E não expira enquanto a entrada pode estar compensando
 *     (CARENCIA_COMPENSACAO_DIAS depois do vencimento dela).
 *  4. Só então assinatura + entrada.
 */
export function decidir(
  acordo: AcordoParaAvaliar,
  assinatura: EstadoAssinatura,
  entrada: EstadoEntrada,
  agoraIso: string,
): Decisao {
  // --- acordos já vigentes: só cuidar do sinal de atraso (Q10)
  if (acordo.status === 'ativo' || acordo.status === 'em_atraso') {
    const temVencida = acordo.vencimentosEmAberto.some((v) => venceuAte(v, agoraIso));
    if (acordo.status === 'ativo' && temVencida) {
      return { acao: 'marcar_atraso', motivo: 'Há parcela vencida em aberto.' };
    }
    if (acordo.status === 'em_atraso' && !temVencida) {
      return { acao: 'sair_do_atraso', motivo: 'Não há mais parcela vencida em aberto.' };
    }
    return { acao: 'nada', motivo: 'Acordo vigente, sem mudança de situação.' };
  }

  // --- daqui para baixo, só estados pendentes
  if (acordo.status !== 'aguardando_assinatura' && acordo.status !== 'aguardando_entrada') {
    return { acao: 'nada', motivo: `Estado ${acordo.status} não é avaliado pela varredura.` };
  }

  if (assinatura.rejeitadoEm) {
    return {
      acao: 'nada',
      motivo: `Assinatura rejeitada em ${assinatura.rejeitadoEm} — o acordo não pode ser efetivado. Refaça o termo.`,
    };
  }

  const assinadoEm = acordo.termoAssinadoEm ?? assinatura.assinadoEm;
  const pagoEm = acordo.entradaPagaEm ?? (entrada.pago ? entrada.pagoEm : null);
  const completo = !!assinadoEm && !!pagoEm;

  // Expirou: só expira quem NÃO completou. Quem completou dentro ou fora do
  // prazo tem direito ao acordo — o cliente cumpriu a parte dele.
  const entradaAindaCompensando =
    !pagoEm && !!acordo.entradaVencimento &&
    agoraIso.slice(0, 10) <= limiteCompensacaoEntrada(acordo.entradaVencimento);

  // Entrada paga e termo sem assinatura (#6342): o cliente já pagou, expirar
  // cancelaria as parcelas e deixaria o dinheiro sem contrato. O acordo segue
  // esperando a assinatura e a cobrança é avisada para correr atrás dela.
  if (!assinadoEm && pagoEm && acordo.expiraEm && acordo.expiraEm <= agoraIso) {
    return {
      acao: 'segurar_sem_assinatura',
      motivo: 'Prazo de validade vencido sem a assinatura do cliente, mas a entrada já foi paga — o acordo não expira.',
    };
  }

  if (!completo && acordo.expiraEm && acordo.expiraEm <= agoraIso && !entradaAindaCompensando) {
    const faltou = [
      !assinadoEm ? 'assinatura do cliente' : null,
      !pagoEm ? 'pagamento da entrada' : null,
    ].filter(Boolean).join(' e ');
    return { acao: 'expirar', motivo: `Prazo de validade vencido sem ${faltou}.` };
  }

  if (completo) {
    return {
      acao: 'efetivar',
      motivo: 'Termo assinado pelo cliente e entrada compensada.',
      assinadoEm,
      entradaPagaEm: pagoEm,
    };
  }

  if (assinadoEm && acordo.status === 'aguardando_assinatura') {
    return {
      acao: 'marcar_assinado',
      motivo: 'Termo assinado pelo cliente; aguardando a compensação da entrada.',
      assinadoEm,
    };
  }

  const pendencia = !assinadoEm
    ? (assinatura.temDocumento ? 'aguardando a assinatura do cliente' : 'o termo ainda não foi gerado')
    : (entrada.temCobranca ? 'aguardando a compensação da entrada' : 'a cobrança da entrada não foi gerada');
  return { acao: 'nada', motivo: `Sem mudança: ${pendencia}.` };
}

export interface LinhaFatura {
  status_boleto_vigor?: string | null;
  data_pgto_boleto?: string | null;
  quitado_em?: string | null;
}

/**
 * A entrada está paga?
 *
 * Regra "campo OU data", a mesma de `_shared/statusBoleto.ts`: 197 boletos da
 * base têm status 'Pago' SEM data (a confirmação manual só grava o status), e as
 * datas `data_pgto_boleto`/`quitado_em` são confiáveis no sentido inverso.
 * Olhar só um dos dois deixaria entrada paga passando por não paga — e o acordo
 * do cliente que já pagou expiraria.
 */
export function entradaEstaPaga(fatura: LinhaFatura | null | undefined): EstadoEntrada {
  if (!fatura) return { temCobranca: false, pago: false, pagoEm: null };
  const status = (fatura.status_boleto_vigor || '').trim();
  const data = fatura.data_pgto_boleto || fatura.quitado_em || null;
  const pago = status === 'Pago' || status === 'Quitado' || !!data;
  return { temCobranca: true, pago, pagoEm: pago ? data : null };
}

// ---------------------------------------------------------------------------
// Conferência das faturas originais antes de efetivar
// ---------------------------------------------------------------------------

export interface FaturaSnapshot {
  id: string | number;
  status_boleto_vigor?: string | null;
  status_fatura?: string | null;
  valor_boleto_vigor?: number | string | null;
}

export interface FaturaAtual {
  id: string | number;
  status_boleto_vigor?: string | null;
  status_fatura?: string | null;
  valor_boleto_vigor?: number | string | null;
  parcelamento_origem_id?: string | null;
  data_pgto_boleto?: string | null;
  quitado_em?: string | null;
}

export interface ProblemaFaturaMae {
  faturaId: string;
  motivo: string;
}

const centavos = (v: unknown) => Math.round(Number(v ?? 0) * 100);

/**
 * As faturas originais ainda estão como estavam quando o acordo foi calculado?
 *
 * Efetivar carimba 'Parcelado' em todas elas. Se uma foi paga no meio tempo, o
 * cliente pagaria de novo nas parcelas; se já está em outro acordo, seria
 * cobrada duas vezes; se mudou de valor, o acordo foi calculado sobre um número
 * que não existe mais. Qualquer um desses casos barra a efetivação — quem
 * resolve é uma pessoa, não a varredura.
 */
export function conferirFaturasMae(
  snapshot: FaturaSnapshot[],
  atuais: FaturaAtual[],
  acordoId: string,
): ProblemaFaturaMae[] {
  const porId = new Map(atuais.map((f) => [String(f.id), f]));
  const problemas: ProblemaFaturaMae[] = [];

  for (const snap of snapshot) {
    const id = String(snap.id);
    const f = porId.get(id);
    if (!f) {
      problemas.push({ faturaId: id, motivo: 'não existe mais' });
      continue;
    }
    const status = (f.status_boleto_vigor || '').trim();
    const origem = f.parcelamento_origem_id || null;
    const jaEsteAcordo = origem === acordoId;

    if (origem && !jaEsteAcordo) {
      problemas.push({ faturaId: id, motivo: 'já está em outro acordo' });
      continue;
    }
    // Carimbada por uma execução anterior deste mesmo acordo: é retomada, não problema.
    if (jaEsteAcordo && status === 'Parcelado') continue;

    if (status === 'Pago' || status === 'Quitado' || f.data_pgto_boleto || f.quitado_em) {
      problemas.push({ faturaId: id, motivo: 'foi paga depois de o acordo ser proposto' });
      continue;
    }
    if (['Cancelado', 'Cancelada', 'Parcelado'].includes(status) || (f.status_fatura || '') === 'Excluído') {
      problemas.push({ faturaId: id, motivo: `está como "${f.status_fatura === 'Excluído' ? 'Excluído' : status}"` });
      continue;
    }
    if (snap.valor_boleto_vigor !== undefined && snap.valor_boleto_vigor !== null
        && centavos(snap.valor_boleto_vigor) !== centavos(f.valor_boleto_vigor)) {
      problemas.push({
        faturaId: id,
        motivo: `mudou de valor (era ${Number(snap.valor_boleto_vigor).toFixed(2)}, agora ${Number(f.valor_boleto_vigor ?? 0).toFixed(2)})`,
      });
    }
  }
  return problemas;
}

/**
 * Status para devolver uma fatura original quando o acordo que a cobria é
 * cancelado. Com snapshot, volta ao que era. Sem snapshot (acordos anteriores a
 * este chamado), decide pelo vencimento — é o que a fatura seria hoje se o
 * acordo nunca tivesse existido.
 */
export function statusRestauracao(
  snap: FaturaSnapshot | null | undefined,
  vencimento: string | null | undefined,
  hojeIso: string,
): string {
  const doSnap = (snap?.status_boleto_vigor || '').trim();
  if (doSnap && doSnap !== 'Parcelado') return doSnap;
  if (vencimento && vencimento.slice(0, 10) < hojeIso.slice(0, 10)) return 'Vencido';
  return 'Em aberto';
}

// ---------------------------------------------------------------------------
// Quem cancela o acordo
// ---------------------------------------------------------------------------

/** Cancelam e efetivam qualquer acordo. */
export const PAPEIS_ALCADA = ['adm', 'gerente'] as const;

/** Acordo que o analista cancela porque ainda não vale: a proposta não
 *  assinada (21/09/2026) e a assinada que espera a entrada (#6268). Enquanto
 *  a entrada não é paga, as faturas originais não foram tocadas — cancelar só
 *  derruba o boleto da entrada. */
export const ESTADOS_CANCELAVEIS_ANALISTA = ['aguardando_aprovacao', 'aguardando_assinatura', 'aguardando_entrada'] as const;

export interface AcordoParaCancelar {
  status: string;
  faturas_origem_snapshot?: unknown;
  termo_id_autentique?: string | null;
}

/**
 * Acordo feito antes do parcelamento atual (#5686, 15/09/2026): não guardou o
 * retrato das faturas originais e não teve termo de confissão de dívida.
 */
export function acordoAntigo(acordo: AcordoParaCancelar): boolean {
  return !acordo.faturas_origem_snapshot && !acordo.termo_id_autentique;
}

/**
 * O papel pode cancelar este acordo?
 *
 * Administrador e gerente cancelam qualquer um. O analista cancela:
 *  - o acordo que ainda não vale: a proposta não assinada (21/09/2026) —
 *    quem monta o acordo é ele, e um dado errado no termo virava beco sem
 *    saída — e a assinada que espera a entrada (#6268): o cliente desiste
 *    antes de pagar e o acordo ficava parado esperando um gerente;
 *  - o acordo antigo em vigor (#6268): sem termo assinado, não há contrato a
 *    desfazer. O cliente que parou de pagar precisa de um acordo novo, com
 *    termo, e o antigo tem de sair do caminho.
 * O acordo novo que já vale (entrada paga) continua com a alçada: cancelar ali
 * devolve as faturas originais e baixa os boletos das parcelas.
 */
export function podeCancelarAcordo(papel: string | null | undefined, acordo: AcordoParaCancelar): boolean {
  if (acordo.status === 'cancelado' || acordo.status === 'expirado') return false;
  if (papel && (PAPEIS_ALCADA as readonly string[]).includes(papel)) return true;
  if (papel !== 'analista') return false;
  if ((ESTADOS_CANCELAVEIS_ANALISTA as readonly string[]).includes(acordo.status)) return true;
  return acordoAntigo(acordo) && (acordo.status === 'ativo' || acordo.status === 'em_atraso');
}

export interface ParcelaParaCancelar {
  numero_parcela?: number | null;
  status?: string | null;
  valor?: number | string | null;
  fatura_id?: string | number | null;
}

export interface FaturaDaParcela extends FaturaAtual {
  mes_referencia?: string | null;
}

export interface ParcelaSomada {
  numero: number | null;
  valor: number;
  faturaId: string;
  mes: string;
}

/**
 * Parcelas que foram somadas na fatura do mês, e não cobradas à parte.
 *
 * Até 20/08/2026 o acordo não gerava cobrança própria para a parcela: o cálculo
 * da fatura mensal achava a parcela do mês e somava no valor. Cancelar o acordo
 * marca a parcela como cancelada, mas a fatura do mês continua cobrando esse
 * valor — e as faturas originais voltam inteiras. Sem o aviso, o acordo novo
 * cobra a mesma parcela duas vezes.
 *
 * `abertas`: a fatura do mês ainda não foi paga. `pagas`: a fatura foi paga,
 * mas a parcela não ficou marcada como paga — o dinheiro entrou e precisa ser
 * abatido como as outras parcelas pagas.
 *
 * Fica de fora a cobrança do próprio acordo (`parcelamento_origem_id` dele),
 * que o cancelamento derruba junto, e a fatura cancelada, que não cobra nada.
 */
export function parcelasSomadasEmFatura(
  parcelas: ParcelaParaCancelar[],
  faturas: FaturaDaParcela[],
  acordoId: string,
): { abertas: ParcelaSomada[]; pagas: ParcelaSomada[] } {
  const porId = new Map(faturas.map((f) => [String(f.id), f]));
  const abertas: ParcelaSomada[] = [];
  const pagas: ParcelaSomada[] = [];
  for (const p of parcelas) {
    if (p.status === 'pago' || p.status === 'cancelado' || p.fatura_id === null || p.fatura_id === undefined) continue;
    const valor = Number(p.valor ?? 0);
    if (!(valor > 0)) continue;
    const f = porId.get(String(p.fatura_id));
    if (!f || f.parcelamento_origem_id === acordoId) continue;
    const status = (f.status_boleto_vigor || '').trim();
    if (['Cancelado', 'Cancelada'].includes(status) || ['Cancelada', 'Excluído'].includes(f.status_fatura || '')) continue;
    const somada = { numero: p.numero_parcela ?? null, valor, faturaId: String(f.id), mes: f.mes_referencia || `fatura ${f.id}` };
    (entradaEstaPaga(f).pago ? pagas : abertas).push(somada);
  }
  return { abertas, pagas };
}

/**
 * Faturas de um acordo encerrado (expirado/cancelado) que aparecem pagas sem
 * que o pagamento faça parte do acordo: a entrada paga depois da expiração, ou
 * a parcela paga depois do cancelamento. É dinheiro sem contrato — alguém
 * precisa devolver ou reaproveitar.
 *
 * `pagasNoAcordo` são as faturas das parcelas que já estavam pagas quando o
 * acordo terminou; essas são legítimas.
 */
export function pagamentosAposEncerramento(
  faturasDoAcordo: Array<FaturaAtual & { quitado_em?: string | null }>,
  pagasNoAcordo: Array<string | number>,
): string[] {
  const legitimas = new Set(pagasNoAcordo.map(String));
  return faturasDoAcordo
    .filter((f) => (f.status_boleto_vigor || '').trim() !== 'Parcelado')
    .filter((f) => entradaEstaPaga(f).pago)
    .map((f) => String(f.id))
    .filter((id) => !legitimas.has(id));
}
